<template>
  <v-badge offset-x="3" color="error" ref="activator" :content="readNotificationsCount" :model-value="readNotificationsCount > 0" @click="openNotifications" title="Notifications">
    <v-icon icon="mdi-bell" size="x-large" @click.stop="openNotifications" title="Notifications"></v-icon>
    
    <v-overlay v-model="overlay" activator="parent" location-strategy="connected" close-on-content-click scroll-strategy="block">
      <v-card class="mx-auto px-0 py-0 rounded-5 tech_notifications_panel" color="#fff" theme="light" width="350px" max-width="350" elevation="2">
        <!-- Show skeleton loader when loading is true -->
        <v-skeleton-loader v-if="loading" v-for="n in 4" :key="n" :elevation="0" type="avatar, sentences" max-width="600"></v-skeleton-loader>

        <!-- Show the User Notifications after loading is completed -->
        <v-list-item v-if="noNotifications" id="tech_notifications_item" class="w-100 px-3 py-2">
          <span class="d-flex" style="justify-content: space-between; align-items: center; gap: 15px">
            <div class="d-flex" style="gap: 10px; align-items: center">
              <v-avatar class="tech_notifications_avatar" :image="getResourceUrl('maya/icons/hot-pink-icon.png')" size="38" rounded="50"></v-avatar>
              <span class="tech_notifications_container">
                <v-list-item-title class="tech_notifications_title lang_translate">No Notifications</v-list-item-title>
              </span>
            </div>
          </span>
        </v-list-item>
        <v-virtual-scroll :items="formattedNotifications" item-height="0">
          <template v-slot:default="{ item }">
            <v-card flat :to="'/' + item.config.href" rel="noopener" :class="{ 'read-notification': item.read, 'unread-notification': !item.read }">
              <v-list-item id="tech_notifications_item" class="w-100 px-3 py-2">
                <span class="d-flex" style="justify-content: space-between; align-items: center; gap: 15px">
                  <div class="d-flex" style="gap: 10px; align-items: center">
                    <v-avatar class="tech_notifications_avatar" :image="getResourceUrl('/' + item.image)" size="38" rounded="50"></v-avatar>
                    <span class="tech_notifications_container">
                      <v-list-item-title class="tech_notifications_title lang_translate">{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle class="tech_notifications_subtitle lang_translate">{{ item.source }}</v-list-item-subtitle>
                    </span>
                  </div>
                  <div class="justify-self-end">
                    <span>{{ item.time }}</span>
                  </div>
                </span>
              </v-list-item>
            </v-card>
          </template>
        </v-virtual-scroll>
      </v-card>
      <!-- message to show no messages -->
      <!-- <v-card class="tech_search_suggestion mx-auto" max-width="600">
  <div class="tech_suggestion_text">Looking for someone and can't find them?</div>
  <button class="tech_primary_button" type="button" onclick="location.href='https://support.squashlevels.com/hc/en-us/articles/7713018013853-I-can-t-find-a-player-on-the-system'" target="_blank">Learn more</button>
</v-card> -->
    </v-overlay>
  </v-badge>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import { useUserDataStore } from '@/store/userDataStore'; // Import Pinia store
import { getResourceUrl } from '@/services/admin_actions/apiUtils'; // Import to use in components

import Icon from '@/components/ComponentLibrary/Icon.vue';

const userDataStore = useUserDataStore(); // Initialize the Pinia store
const overlay = ref(false);
const loading = ref(true); // Initial state as loading

interface NotificationItem {
  noteid: string;
  notetypeid: string;
  title: string;
  notestatus: string;
  personid: string;
  dateint: string;
  config: {
    cmsid: string;
    href: string;
    full_groupid: string;
  };
  image: string;
  time: string;
  source: string;
  read: boolean;
}

const notifications = computed(() => userDataStore.notifications); // Assuming notifications are stored here

const readNotificationsCount = computed(() => {
  return notifications.value.filter((notification: NotificationItem) => !notification.read).length;
});

const noNotifications = computed(() => {
  return notifications.value.length === 0;
});

const formattedNotifications = computed<NotificationItem[]>(() => {
  // Get the last 20 notifications, or all if fewer than 20
  return notifications.value.slice(0, 20).map((notification: NotificationItem) => {
    return {
      ...notification,
    };
  });
});

onMounted(() => {
  loading.value = false;
});

const openNotifications = () => {
  // console.groupCollapsed('Open Notifications');
  overlay.value = true;
  // console.log('Overlay opened, attempting to mark notifications as read.');

  setTimeout(() => {
    if (typeof userDataStore.markNotificationsAsRead === 'function') {
      // console.log('markNotificationsAsRead function exists, calling it.');
      userDataStore.markNotificationsAsRead();
    } else {
      console.warn('markNotificationsAsRead is not defined in the store.');
    }
  }, 1000);

  // console.groupEnd();
};
</script>

<style lang="scss" scoped>
// General Styling For the Notifications Skeleton Loader
.tech_notifications_skeleton {
  background-color: $white;
  max-width: 600px;
  padding: 10px;
  border-radius: 5px;
  margin-top: 0px;
}

// General Styling For the Notifications Panel
.tech_notifications_panel {
  margin: 0px 0px;
  height: 100%;
  height: calc(100dvh - 64px);
  max-height: 600px;
  max-width: 350px;
  // z-index: 1000;
  // position: relative;
  overflow: auto;
  border-radius: 5px;
  margin-top: 18px;
}

// Media Queries change of Notification Panel Location on screens larger than mobile
@media only screen and (min-width: $mobile-screen) {
  .tech_notifications_panel {
    right: -30%;
  }
}

// Hide Scrollbar on Notifications Panel
.tech_notifications_panel::-webkit-scrollbar {
  display: none;
}

// Remove Margin from the Notifications Results Avatar
.tech_notifications_avatar {
  margin: 0px;
  border-radius: 50px;
}

// Style Notifications Title text
.tech_notifications_title {
  font-family: $english-grotesque-font, sans-serif;
  color: $site-purple;
  opacity: 1;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.1rem;
  text-align: left;
  white-space: pre-wrap;
}

// Display Subtitle text as flex and align to left
.tech_notifications_subtitle {
  display: flex;
  align-items: flex-start;
  text-align: left;
  font-family: $brandon-grotesque-font, sans-serif;
  color: $site-purple;
  opacity: 1;
  font-weight: 600;
  font-size: 13px;
}

// Add Color to Title and Subtitle on Hover
.tech_notifications_container {
  &:hover {
    .tech_notifications_title,
    .tech_notifications_subtitle {
      color: $secondary;
    }
  }
}

// Remove Border-Radius from Results Card on Search Page
.v-card--flat {
  border-radius: 0px;
}

// Add border to unread notifications
.unread-notification {
  border-left: 4px solid $teal; /* Style for read items */
}

// Add border between all notification list items
#tech_notifications_item {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: inset 0 -1px 0 0 rgba(0, 0, 0, 0.1);
}

#tech_notifications_item:first-child {
  border-top: 0px solid transparent;
}

// Add Cursor Pointer to Notifications Bell
.mdi-bell {
  cursor: pointer;
}

.mdi-bell::before {
  color: rgb(var(--v-theme-primary-700));
  font-size: 26px;
  opacity: 1;
}

.mdi-bell:hover::before {
  color: rgb(var(--v-theme-highlight));
  transition: all 0.1s;
  -moz-transition: all 0.1s;
  -webkit-transition: all 0.1s;
  -o-transition: all 0.1s;
}

// Media Queries for User Tech Bar Component on screens less than 1100px
@media only screen and (max-width: $mobile-screen) {
  // Change Bell Icon to White and decrease size on Screens less than 1100px
  .mdi-bell::before {
    color: $white;
    font-size: 22px;
  }
}
</style>
